import React, { useState } from 'react';
import { Card, Row, Col } from '@/components/ui';
import { Project } from '../../../types/Project';
import { formatDollars } from '../../../utils/formatDollars';

interface ProjectCostsBreakdownProps {
  project: Project;
}

const ProjectCostsBreakdown: React.FC<ProjectCostsBreakdownProps> = ({ project }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Use aggregated totals from backend (calculated from ALL tasks, not just paginated ones)
  // Fallback to calculating from visible tasks if aggregated totals not available
  const costs = project.aggregatedTotals?.baseCosts || {
    vehicle: 0,
    material: 0,
    labour: 0,
    subcontractor: 0,
    hiredEquipment: 0,
    other: 0,
    total: 0
  };

  const getPercentage = (amount: number) => {
    return costs.total > 0 ? ((amount / costs.total) * 100).toFixed(1) + '%' : '0%';
  };

  const getBudgetStatusColor = () => {
    if (!project.budget || project.budget === 0) return 'tw-text-content';
    return costs.total <= project.budget ? 'tw-text-success' : 'tw-text-danger';
  };

  return (
    <Row>
      {/* Vehicle Box */}
      <Col md={2}>
        <Card className="tw-h-full">
          <Card.Body className="tw-p-2">
            <h6 className="tw-mb-1 tw-text-content-muted">Vehicle</h6>
            <p className="tw-mb-0">
              {formatDollars(costs.vehicle)}
              <small className="tw-block tw-text-content-muted">{getPercentage(costs.vehicle)}</small>
            </p>
          </Card.Body>
        </Card>
      </Col>

      {/* Material Box */}
      <Col md={2}>
        <Card className="tw-h-full">
          <Card.Body className="tw-p-2">
            <h6 className="tw-mb-1 tw-text-content-muted">Material</h6>
            <p className="tw-mb-0">
              {formatDollars(costs.material)}
              <small className="tw-block tw-text-content-muted">{getPercentage(costs.material)}</small>
            </p>
          </Card.Body>
        </Card>
      </Col>

      {/* Labour Box */}
      <Col md={2}>
        <Card className="tw-h-full">
          <Card.Body className="tw-p-2">
            <h6 className="tw-mb-1 tw-text-content-muted">Labour</h6>
            <p className="tw-mb-0">
              {formatDollars(costs.labour)}
              <small className="tw-block tw-text-content-muted">{getPercentage(costs.labour)}</small>
            </p>
          </Card.Body>
        </Card>
      </Col>

      {/* Subcontractor Box */}
      <Col md={2}>
        <Card className="tw-h-full">
          <Card.Body className="tw-p-2">
            <h6 className="tw-mb-1 tw-text-content-muted">Subcontractor</h6>
            <p className="tw-mb-0">
              {formatDollars(costs.subcontractor)}
              <small className="tw-block tw-text-content-muted">{getPercentage(costs.subcontractor)}</small>
            </p>
          </Card.Body>
        </Card>
      </Col>

      {/* Hired Equipment Box */}
      <Col md={2}>
        <Card className="tw-h-full">
          <Card.Body className="tw-p-2">
            <h6 className="tw-mb-1 tw-text-content-muted">Hired Equipment</h6>
            <p className="tw-mb-0">
              {formatDollars(costs.hiredEquipment)}
              <small className="tw-block tw-text-content-muted">{getPercentage(costs.hiredEquipment)}</small>
            </p>
          </Card.Body>
        </Card>
      </Col>

      {/* Other Costs Box */}
      <Col md={2}>
        <Card className="tw-h-full">
          <Card.Body className="tw-p-2">
            <h6 className="tw-mb-1 tw-text-content-muted">Other Costs</h6>
            <p className="tw-mb-0">
              {formatDollars(costs.other)}
              <small className="tw-block tw-text-content-muted">{getPercentage(costs.other)}</small>
            </p>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default ProjectCostsBreakdown; 